/**
 * Number / date formatting. Everything goes through Intl so locale rules (grouping, currency,
 * rounding) live in one place.
 *
 * Money convention (app-wide, FP&A's rule): negative amounts — credits, reversals, a department
 * whose QTD total is negative — render in accounting parentheses, `($5,125)`, never with a minus
 * sign. Variances add a `+` when over budget: `+$1,200` / `($300)`. Totals are summed in integer
 * cents (`sumMoney`) so a displayed total equals the sum of the rows it is made of.
 */
const LOCALE = 'en-US'
const TIME_ZONE = 'America/New_York'

const usd0 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const usd2 = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const compact1 = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 })
const integer = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 })
const dateTime = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: TIME_ZONE,
})
const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })

export interface MoneyOptions {
  /** `$12.3k` / `$1.4M` instead of full digits (amounts ≥ $1,000). */
  compact?: boolean
  /** Show cents. Ignored when `compact` applies. */
  cents?: boolean
}

export function formatMoney(value: number, options: MoneyOptions = {}): string {
  if (!Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  let text: string
  if (options.compact && abs >= 1_000_000) text = `$${compact1.format(abs / 1_000_000)}M`
  else if (options.compact && abs >= 1_000) text = `$${compact1.format(abs / 1_000)}k`
  else text = (options.cents ? usd2 : usd0).format(abs)
  // Rounding can turn a tiny negative into "$0"; don't wrap zero in parentheses.
  const isZero = text === '$0' || text === '$0.00'
  return value < 0 && !isZero ? `(${text})` : text
}

/** Signed variance in money: `+$1,200` over, `($300)` under. */
export function formatSignedMoney(value: number, options: MoneyOptions = {}): string {
  return value > 0 ? `+${formatMoney(value, options)}` : formatMoney(value, options)
}

/** Signed percent, e.g. `+4.2%`. Null/NaN → em dash. */
export function formatSignedPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const fixed = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
  return value > 0 ? `+${fixed}%` : `${fixed}%`
}

/** Whole percent, e.g. `62%`. Null → em dash. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${integer.format(value)}%`
}

/** Sum of 2-decimal amounts, exact: accumulated in integer cents (no float drift). */
export function sumMoney<T>(items: readonly T[], amount: (item: T) => number): number {
  const cents = items.reduce((sum, item) => sum + Math.round(amount(item) * 100), 0)
  return cents / 100 || 0
}

const isoDay = new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

/** `YYYY-MM-DD` (a calendar day, no time zone) → `Oct 14, 2026`. */
export function formatIsoDay(value: string | null | undefined): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '—'
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? '—' : isoDay.format(date)
}

export function formatCount(value: number): string {
  return integer.format(value)
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural
}

/** `Aug 26, 3:05 PM` in America/New_York. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : dateTime.format(date)
}

const dateOnly = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'America/New_York',
})

/** `Oct 3, 2026` in America/New_York. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : dateOnly.format(date)
}

/** `5 minutes ago`, `yesterday`, … relative to `now`. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 60) return relative.format(seconds, 'second')
  if (abs < 3_600) return relative.format(Math.round(seconds / 60), 'minute')
  if (abs < 86_400) return relative.format(Math.round(seconds / 3_600), 'hour')
  return relative.format(Math.round(seconds / 86_400), 'day')
}
